/* eslint-disable complexity */
import { SelectChangeEvent } from '@mui/material';
import { IProductDataContext } from '../@types/ContextProviderRelated';
import FilesAPI2DModel from '../models/standard/FilesAPI2DModel';
import FilesAPIProductModel from '../models/standard/FilesAPIProductModel';

export const searchInFetchedData = (
  providerData: IProductDataContext,
  searchString: string,
  filteredManufacturers: string[],
  filteredCategories: string[],
  allManufacturersLabel: string,
  allCategoriesLabel: string,
): FilesAPIProductModel[] => {
  // Create return array.
  const results: FilesAPIProductModel[] = [];
  const searchStr = searchString.toLowerCase();

  // Check if the search string is a number to enforce exact matching.
  const isNumberSearch = /^\d+$/.test(searchString);

  for (const product of providerData.products) {
    // Check if the product matches the search string with exact or fuzzy match based on search type.
    const matchesSearch = searchStr === '' || checkForStringMatchOnAnyValue(product, searchStr, !isNumberSearch);

    // Check if the product matches the selected manufacturers.
    const matchesManufacturer = filteredManufacturers.includes(allManufacturersLabel) || filteredManufacturers.includes(product.manufacturer?.name);

    // Check if the product matches the selected categories (objects).
    const matchesCategory = filteredCategories.includes(allCategoriesLabel) || product.objects?.some((obj) => filteredCategories.includes(obj.name));

    // When all conditions are met, add the product to the results.
    if (matchesSearch && matchesManufacturer && matchesCategory) {
      results.push(product);
    }
  }

  return results;
};

// TODO: Add a search function part that also checks the modelAssets and materialAssets.
/**
 * Function to check a search string against every single field, value and key of a given product model.
 * If any match is found, the function will return true, which indicates, that a match was successful.
 * Can now also be used for exact matches only.
 *
 * @param {FilesAPIProductModel} product the product model that needs to have its values matched against.
 * @param {string} str the string that is the search request parameter.
 * @param {boolean} fuzzyMatch optional flag to indicate if the search should match on partial strings or only when a perfect match is found. Defaults to false.
 * @returns {boolean} true if any match was found. False otherwise.
 */
export const checkForStringMatchOnAnyValue = (product: FilesAPIProductModel, str: string, fuzzyMatch: boolean = false): boolean => {
  const searchStr = str.toLowerCase();

  // Helper function for case-insensitive "includes" check
  const includesIgnoreCase = (value: any) => value !== null && value !== undefined && value.toString().toLowerCase().includes(searchStr);

  // Helper function for case-insensitive "equals" check
  const equalsIgnoreCase = (value: any) => value !== null && value !== undefined && value.toString().toLowerCase() === searchStr;

  // Select the comparator logic based on the mode
  const comparator = fuzzyMatch ? includesIgnoreCase : equalsIgnoreCase;

  // Function to check a list of values for a match
  const checkValues = (values: any[]) => values.some(comparator);

  // 1. Check direct fields
  if (
    checkValues([
      product.name,
      product.description,
      product.productId,
      product.manufacturer?.name,
      product.previewImagePath,
      product.previewPicture?.fileName,
      product.previewPicture?.filePath,
      product.depth,
      product.length,
    ])
  ) {
    return true;
  }

  // 2. Check objects (Categories)
  if (product.objects?.some((obj) => comparator(obj.name))) {
    return true;
  }

  // 3. Check attributes (Fix based on your log structure)
  if (product.attributes && Array.isArray(product.attributes)) {
    for (const prodAttr of product.attributes) {
      // A. Check the Value (e.g., 1.33, "Option1", true)
      // Note: The log shows 'value' directly on the object, generic for all types
      if (comparator(prodAttr.value)) return true;

      // B. Check the Attribute Definition (nested inside .attribute)
      // We cast to 'any' here briefly because the interface might not reflect the nested structure perfectly yet
      const def = (prodAttr as any).attribute;

      if (def) {
        // Check technical key (e.g., "Wall thickness")
        if (comparator(def.key)) return true;

        // Check localized keys (e.g., "Wandstärke")
        if (def.localization) {
          const localizations = Object.values(def.localization);
          if (localizations.some((loc) => comparator(loc))) {
            return true;
          }
        }
      }
    }
  }

  // 4. Check assets
  if (product.assets) {
    for (const asset of product.assets) {
      if (checkValues([asset.name, asset.assetType, asset.assetType?.toString(), asset.ownerId?.toString()]) || (asset.object && comparator(asset.object.name))) {
        return true;
      }
    }
  }

  // When no match is found, return false
  return false;
};

/**
 * Function to handle the filtering of the categories according to user input in the select fields.
 *
 * @param e the change event that was triggered when the user selected a new category from the select dropdown.
 * @param localizedLabel the localized label for the select options to display all categories when none are selected.
 * @returns {string[]} the categories in a string array.
 */
export const handleFilteringCategories = (e: SelectChangeEvent<string[]>, localizedLabel: string): string[] => {
  const newSelection = e.target.value;
  if (newSelection === '') {
    return [localizedLabel];
  }
  let tmpArr = [];
  if (typeof newSelection === 'string') {
    tmpArr = newSelection.split(',');
    const index = tmpArr.indexOf(localizedLabel);
    if (index !== -1) {
      tmpArr.splice(index, 1);
    }
    return removeLocalizedLabel([...tmpArr], localizedLabel);
  }
  if (newSelection.length === 0) {
    return [localizedLabel];
  }
  if (newSelection[newSelection.length - 1] === localizedLabel) {
    return [localizedLabel];
  }
  const index = newSelection.indexOf(localizedLabel);
  if (index !== -1) {
    newSelection.splice(index, 1);
  }
  return removeLocalizedLabel([...newSelection], localizedLabel);
};

/**
 * Function to process the change events from the select menu containing the manufacturers.
 * Any selected values will get added to any search query so that the results will come back filtered accordingly.
 * But since we added the option to select all manufacturers, we need to check if the user selected this option last.
 * If he did, we need to remove all other selections and search without filtering for manufacturers specifically.
 * And if he did not, we iterate over just to make sure, that it has not ended up at a different index.
 * And if we find it, we remove it.
 *
 * @param {SelectChangeEvent} e event that was triggered when a change was made by a user. Contains the last added value as its target.
 */
export const handleFilteringManufacturers = (e: SelectChangeEvent<string[]>, localizedLabel: string): string[] => {
  const newSelection = e.target.value;
  if (typeof newSelection === 'string') {
    if (newSelection === '') {
      return removeLocalizedLabel([localizedLabel], localizedLabel);
    }
    const tmpArr = newSelection.split(',');
    const index = tmpArr.indexOf(localizedLabel);
    if (index !== -1) {
      tmpArr.splice(index, 1);
    }
    return removeLocalizedLabel([...tmpArr], localizedLabel);
  }
  if (newSelection.length === 0) {
    return [localizedLabel];
  }
  const index = newSelection.indexOf(localizedLabel);
  if (index !== -1) {
    newSelection.splice(index, 1);
    return removeLocalizedLabel([...newSelection], localizedLabel);
  }
  return newSelection;
};

/**
 * Function to remove the localized label text from a value array containing categories, attributes or manufacturers.
 * If the array is empty or it only contains a single element, we return only the label in the array.
 * For any other case, the label gets spliced and then the array gets returned.
 *
 * @param {string[]} values the string values that need to get checked.
 * @param {string} localizedLabel the string that needs to might need to get purged from the array.
 * @returns {string[]} the array with the localized label removed, when it does not fit.
 */
export const removeLocalizedLabel = (values: string[], localizedLabel: string): string[] => {
  const index = values.indexOf(localizedLabel);
  switch (values.length) {
    case 0:
      return [...localizedLabel];
    case 1:
      if (values[0] === localizedLabel) {
        return [...localizedLabel];
      }
      return values;
    default:
      if (index === -1) {
        return values;
      }
      values.splice(index, 1);
      return values;
  }
};
/**
 * Function to filter the products from our api products that do not have a size or are otherwise misconfigured.
 *
 * @param {FilesAPIProductModel[]} unfiltered the product model array that has unwanted products in it.
 * @returns {FilesAPIProductModel[]} the array filtered.
 */
export const filterProducts = (unfiltered: FilesAPIProductModel[]): FilesAPIProductModel[] => {
  const filtered = [];
  for (const prod of unfiltered) {
    if (isProductDataComplete(prod)) {
      filtered.push(prod);
    }
  }
  return filtered;
};

/**
 * Function to filter the models2D from our API that do not have necessary properties.
 *
 * @param {FilesAPI2DModel[]} unfilteredModels The raw 2D models array.
 * @returns {FilesAPI2DModel[]} The filtered array with only valid models.
 */
export const filterModels2D = (unfilteredModels: FilesAPI2DModel[]): FilesAPI2DModel[] => unfilteredModels.filter((model) => model.fileName);

/**
 * Function to test a given product to see if all needed fields are present.
 * Will return true, if the product passes all checks.
 *
 * @param {FilesAPIProductModel} product the product for which we need to check its data.
 * @returns {boolean} indicating if the product data set is complete or not.
 */
export const isProductDataComplete = (product: FilesAPIProductModel): boolean => {
  // We only want products that have a width and a height value.
  if (product.length === 0 || product.depth === 0) {
    return false;
  }
  //TODO only objects with an SVG

  // And if all checks have passed, we can return true.
  return true;
};

/**
 * Checks whether a given 2D model matches the search string.
 * It searches in model.name, dimensions, attributes (if any), and the single model.object.
 *
 * @param model The 2D model to check.
 * @param str The search string.
 * @param fuzzyMatch If true, performs a fuzzy (includes) match; otherwise, an exact match.
 * @returns true if any field matches, false otherwise.
 */
export const checkForStringMatchOn2DModel = (model: FilesAPI2DModel, str: string, fuzzyMatch: boolean = false): boolean => {
  const searchStr = str.toLowerCase();

  const includesIgnoreCase = (value: any) => value !== null && value !== undefined && value.toString().toLowerCase().includes(searchStr);

  const equalsIgnoreCase = (value: any) => value !== null && value !== undefined && value.toString().toLowerCase() === searchStr;

  // Check direct fields
  const directFields = [model.name, model.depth, model.length];
  const directMatch = fuzzyMatch ? directFields.some((field) => includesIgnoreCase(field)) : directFields.some((field) => equalsIgnoreCase(field));

  if (directMatch) return true;

  // Check the single object property
  if (model.object) {
    if (fuzzyMatch ? includesIgnoreCase(model.object.name) : equalsIgnoreCase(model.object.name)) {
      return true;
    }
  }

  // If you have attributes on your 2D models, you can include similar logic here.
  // (Omitted if not needed.)

  return false;
};

export const searchInFetchedModels2D = (models: FilesAPI2DModel[], searchString: string, filteredCategories: string[], allCategoriesLabel: string): FilesAPI2DModel[] => {
  const searchStr = searchString.toLowerCase();
  const isNumberSearch = /^\d+$/.test(searchString);

  return models.filter((model) => {
    // Check if the model matches the search string.
    const matchesSearch = searchStr === '' || checkForStringMatchOn2DModel(model, searchStr, !isNumberSearch);

    // Check if the model matches the selected categories.
    // If "all categories" is selected, it's automatically a match.
    // Otherwise, check if the model's object name is in the filtered list.
    const matchesCategory = filteredCategories.includes(allCategoriesLabel) || (model.object && filteredCategories.includes(model.object.name));

    return matchesSearch && matchesCategory;
  });
};
